<template>
    <div class="property-select-form-inputs">
        <el-form-item>
            <template #label>
                <span>
                    {{ translate('search_label') }}
                    <el-tooltip
                        class="item"
                        effect="dark"
                        content="Information"
                        placement="top-start"
                    >
                        <i class="el-icon-warning-outline" />
                    </el-tooltip>
                </span>
            </template>
            <el-select
                v-model="search"
                style="width: 100%"
                filterable
                remote
                :disabled="volumePercentage === 100 || !showComponents"
                reserve-keyword
                :remote-method="fetchProperties"
                :placeholder="translate('search_placeholder')"
            >
                <template #prefix>
                    <i class="el-icon-search"></i>
                </template>
                <el-option
                    :label="property.nome"
                    :value="property.id"
                    v-for="property in properties"
                    :key="property.id"
                >
                    {{ property.nome }}
                </el-option>
            </el-select>
        </el-form-item>
        
        <property-selected v-model="selected" />

        <el-divider />
        <template v-if="selected.territory_id">
            <p>{{ translate('invoice_number_and_attachment_title') }}.</p>
            <div class="attachment">
                <el-form-item :label="translate('invoice_number')">
                    <el-input v-model="selected.invoice_number" />
                </el-form-item>
                <el-form-item v-if="!selected.invoice_attachments.length">
                    <template #label>&nbsp;</template>
                    <el-button type="primary" @click="$refs.fileInput.click()">
                        <i class="el-icon-upload2" style="margin-right: 10px" />
                        {{ translate('invoice_attach') }}
                    </el-button>
                    <input
                        ref="fileInput"
                        type="file"
                        multiple
                        style="display: none"
                        accept=".png, .jpeg, .jpg, .pdf"
                        @change="handleUpload"
                    />
                </el-form-item>
                <el-form-item v-if="selected.invoice_attachments.length">
                    <template #label>&nbsp;</template>
                    <div class="attachment-files-wrapper">
                        <div
                            class="attachment-file"
                            v-for="(file, idx) in selected.invoice_attachments"
                            :key="idx"
                        >
                            <div>
                                <span>{{ translate('invoice_file_name') }}:</span>
                                <strong>{{ formatFilename(file.name) }}</strong>
                            </div>
                            <div>
                                <span>{{ translate('invoice_file_size') }}:</span>
                                <strong>{{ formatFileSize(file.size) }}</strong>
                            </div>
                            <div class="attachment-file__actions">
                                <el-tooltip
                                    class="item"
                                    effect="dark"
                                    :content="translate('invoice_file_tooltip_view')"
                                    placement="top-start"
                                >
                                    <i class="el-icon-view" @click="handleFileView(file)" />
                                </el-tooltip>
                                <el-tooltip
                                    class="item"
                                    effect="dark"
                                    :content="translate('invoice_file_tooltip_delete')"
                                    placement="top-start"
                                >
                                    <i class="el-icon-delete" @click="handleFileRemove(file)" />
                                </el-tooltip>
                            </div>
                        </div>
                    </div>
                    <!-- permite escolher mais arquivos -->
                    <div style="margin-top: 10px">
                        <el-button type="primary" @click="$refs.fileInput.click()">
                            <i class="el-icon-upload2" style="margin-right: 10px" />
                            {{ translate('invoice_attach') }}
                        </el-button>
                        <input
                            ref="fileInput"
                            type="file"
                            multiple
                            style="display: none"
                            accept=".png, .jpeg, .jpg, .pdf"
                            @change="handleUpload"
                        />
                    </div>
                </el-form-item>
                <el-form-item>
                    <template #label>&nbsp;</template>
                    <el-button
                        type="primary"
                        :disabled="!canAddToFarmList"
                        @click="handleAddToPropertyList"
                    >
                        <i class="el-icon-upload2" style="margin-right: 10px" />
                        {{ translate('invoice_add_to_farm_list') }}
                    </el-button>
                </el-form-item>
            </div>
        </template>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import PropertySelected from '@/modules/traceability-contract/views/components/property-select/components/property-selected.vue';

export default {
    name: 'PropertySelectDefault',
    components: { PropertySelected },
    props: {
        value: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            search: null,
            selected: {
                territory_id: '',
                volume: '',
                invoice_number: '',
                invoice_attachments: []
            }
        };
    },
    computed: {
        ...mapState('traceabilityContractModule', ['form', 'availableBalance']),
        ...mapGetters('traceabilityContractModule', {
            properties: 'getProperties',
            contratctDistrict: 'getContractDistrict'
        }),
        ...mapGetters('traceabilityContractModule', {
            volumePercentage: 'getVolumePercentage',
            volumeAdded: 'getVolumeAdded'
        }),
        data: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        canAddToFarmList() {
            return (
                this.selected.territory_id &&
                this.selected.volume
            );
        },
        showComponents() {
            return !!this.form.protocol_id;
        }
    },
    watch: {
        async search(val) {
            if (!val) return;
            const current = this.properties.find(
                property => property.id === val
            );
             if (current.id) {
                try {
                    await this.fetchAvailableBalance({
                        id: current.id,
                        locale: this.$i18n.locale,
                        form: this.form
                    });
                } catch (error) {
                    this.resetSelected();
                }
            }
            try {
                const response = await this.fetchEligibility({
                    id: current.id,
                    document: current.docRepresentantes[0],
                    contractDistrict: this.contratctDistrict
                });

                this.selected = {
                    name: current.nome,
                    ruc: current.docRepresentantes ? current.docRepresentantes[0] : '',
                    territory_lyra_code: current.codigoLyra || '',
                    producer_lyra_code: current.representantes ? current.representantes[0].codigoLyra : '',
                    department: 'N/D',
                    district: current.localidade || 'N/D',
                    analysis_date: current.dataAtualizacaoFormatada || 'N/D',
                    harvest: current.harvest || 'N/D',
                    area: current.areaHa || 'N/D',
                    territory_id: current.id,
                    volume: '',
                    invoice_number: '',
                    invoice_attachments: [],
                    eligibility: response,
                    producer_lyra_name: current.representantes ? current.representantes[0].nomeRazaoSocial : ''
                };
            } catch (error) {
                this.resetSelected();
            }
        }
    },
    beforeDestroy() {
        this.$store.commit('traceabilityContractModule/SET_PROPERTIES', []);
    },
    methods: {
        ...mapActions('traceabilityContractModule', [
            'searchProperties',
            'fetchEligibility',
            'fetchAvailableBalance',
            'validateAvailableBalance'
        ]),
        async fetchProperties(query) {
            await this.searchProperties(query);
        },
        handleUpload() {
            const files = Array.from(this.$refs.fileInput.files);

            let invalidExtension = false;
            files.forEach((file) => {
                if (!this.verifyExtensionFile(file)) {
                    invalidExtension = true;
                }
            });

            if (invalidExtension) {
                this.$notify.error({
                    message: this.$t(
                        'contrato_rastreabilidade.messages.errors.invalid_extension'
                    )
                });
                return;
            }

            this.selected.invoice_attachments = [
            ...this.selected.invoice_attachments,
            ...files
            ];
            this.$refs.fileInput.value = '';
        },
        formatFilename(filename) {
            return filename.length > 20
                ? `${filename.slice(0, 20)}...${filename.slice(
                      filename.length - 4,
                      filename.length
                  )}`
                : filename;
        },
        formatFileSize(bytes) {
            const sizes = ['Bytes', 'KB', 'MB'];
            if (bytes === 0) return '0 Bytes';

            const i = Math.floor(Math.log(bytes) / Math.log(1024));
            const fileSize = bytes / Math.pow(1024, i);

            return `${fileSize.toFixed(2)} ${sizes[i]}`;
        },
        handleFileRemove(fileToRemove) {
            this.selected.invoice_attachments = this.selected.invoice_attachments.filter(
                file => file !== fileToRemove
            );
        },
        handleFileView(file) {
            const blob = new Blob([file], { type: file.type });
            const url = URL.createObjectURL(blob);
            window.open(url);
        },
        async handleAddToPropertyList() {
            if (!this.form.contract_data.volume) {
                this.$notify.error({
                    message: this.$t(
                        'contrato_rastreabilidade.messages.errors.transaction_volume_error'
                    )
                });
                return;
            }
            if (this.data.find(property => property.territory_id === this.selected.territory_id)) {
                this.$notify.error({
                    message: this.$t(
                        'contrato_rastreabilidade.messages.errors.property_already_added_error'
                    )
                });
                return;
            }
            const volumeTotalAdded = this.data.reduce(
                (acc, property) => acc + Number(property.volume),
                0
            );

            if (
                Number(volumeTotalAdded) + Number(this.selected.volume) >
                Number(this.form.contract_data.volume)
            ) {
                this.$notify.error({
                    message: this.$t(
                        'contrato_rastreabilidade.messages.errors.volume_exceeded_error'
                    )
                });
                return;
            }
            if (this.selected.invoice_attachments.length > 30) {
                this.$notify.error({
                    message: this.$t(
                        'contrato_rastreabilidade.messages.errors.max_files_error', {maxFiles: 30}
                    )
                });
                return;
            }

            let valid;
            try {
                valid = await this.validateAvailableBalance({
                    id: this.selected.territory_id,
                    extraArea: this.selected.volume,
                    form: this.form
                });
                if (!valid.data) {
                    const backendMsg = valid.message;
                    const errorMsg = backendMsg
                        ? this.$t('contrato_rastreabilidade.messages.errors.backend_available_balance_error', { backendMsg })
                        : this.$t('contrato_rastreabilidade.messages.errors.available_balance_error');
                    this.$notify.error({ message: errorMsg });
                    return;
                }
            } catch (error) {
                const backendMsg = error.response && (error.response.data.message);
                const errorMsg = backendMsg
                    ? this.$t('contrato_rastreabilidade.messages.errors.backend_available_balance_error', { backendMsg })
                    : this.$t('contrato_rastreabilidade.messages.errors.available_balance_error');
                this.$notify.error({ message: errorMsg });
                return;
            }

            this.data.push(this.selected);
            this.search = null;

            await this.fetchAvailableBalance({
                id: this.selected.territory_id,
                locale: this.$i18n.locale,
                form: this.form
            });
            this.resetSelected();
        },
        resetSelected() {
            this.selected = {
                territory_id: '',
                name: '',
                geometry: '',
                harvest: '',
                harvest_year: '',
                analysis_date: '',
                district: '',
                volume: '',
                condition: '',
                diagnostic_result: {},
                invoice_number: '',
                invoice_attachments: []
            };
        },
        verifyExtensionFile(file) {
            return file.name.toLowerCase().endsWith('.pdf') ||
                file.name.toLowerCase().endsWith('.jpg') ||
                file.name.toLowerCase().endsWith('.jpeg') ||
                file.name.toLowerCase().endsWith('.png');
        },
        translate(key) {
            const base = 'contrato_rastreabilidade.messages.report_properties';
            return this.$t(`${base}.${key}`);
        }
    }
};
</script>

<style scoped lang="scss">
.property-select-form-inputs {
    display: grid;
    grid-template-columns: 1fr;
    grid-column-gap: 16px;

    i.el-icon-search {
        color: var(--primary);
        font-size: 14px;
        margin: 12px 4px;
    }

    i.el-icon-warning-outline {
        font-size: 14px;
        transform: rotate(180deg);
    }

    .attachment {
        display: grid;
        grid-template-columns: 1fr 2fr auto;
        grid-column-gap: 16px;
        @media (max-width: 768px) {
            grid-template-columns: 1fr;
            &:last-child .el-button {
                width: 100%;
            }
        }

        .attachment-files-wrapper {
            max-height: 90px;
            overflow-y: auto;
            margin-bottom: 10px;
        }

        .attachment-file {
            background-color: #f5f5f5;
            height: 40px;
            padding: 0 8px;
            border-radius: 4px;
            display: grid;
            grid-template-columns: 2fr 1fr 60px;
            align-items: center;
            margin-bottom: 8px;

            .attachment-file__actions {
                display: flex;
                justify-content: space-between;
                align-items: center;

                .el-icon-view {
                    color: var(--primary);
                    font-weight: bolder;
                    font-size: 14px;
                    padding: 6px;

                    &:hover {
                        cursor: pointer;
                        background-color: rgba(148, 207, 28, 1);
                        box-sizing: border-box;
                        border-radius: 4px;
                    }
                }

                .el-icon-delete {
                    color: var(--danger);
                    font-weight: bolder;
                    font-size: 14px;
                    padding: 6px;

                    &:hover {
                        cursor: pointer;
                        background-color: rgba(244, 193, 190, 1);
                        box-sizing: border-box;
                        border-radius: 4px;
                    }
                }
            }
        }
    }
}
.property-select-form-inputs-opacity {
    opacity: 0.5;
}
</style>
